import { useDispatch, useSelector } from 'react-redux';
import { updateUser,  fetchUserData, selectUser,selectUserLoading  } from '../../../app/store/slices/userSlice';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { locationOptions } from '../../../utils/constants/locationOptions';
import logo from '../../../assets/images/logos/576_Cash Canvas-02.png';
import { selectCanvases, fetchUserCanvases } from '../../../app/store/slices/canvasSlice';
import { fetchUserInvitations } from '../../../app/store/slices/groupSlice';
import { fetchSubscriptionData, selectCurrentPlan } from '../../../app/store/slices/subscriptionSlice';




const ProfileSetupPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Fix user selection
  const userState = useSelector(selectUser);
  const currentPlan = useSelector(selectCurrentPlan);
  const { loading: userLoading } = useSelector(selectUserLoading);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const userCanvases = useSelector(selectCanvases);
  const receivedInvites = useSelector(state => state.group.receivedInvites)

  

  const user = useMemo(() => {
    if (userState?.user) {
      return userState.user;
    }
    try {
      const authData = JSON.parse(localStorage.getItem('authData'));
      return authData?.user || null;
    } catch {
      return null;
    }
  }, [userState]);

  useEffect(() => {
    const fetchData = async () => {
      setIsDataLoading(true);
      try {
        await dispatch(fetchUserData()).unwrap();
        await dispatch(fetchUserCanvases()).unwrap();
        await dispatch(fetchUserInvitations()).unwrap();
        await dispatch(fetchSubscriptionData()).unwrap()
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        const authData = JSON.parse(localStorage.getItem('authData'));
        if (!authData?.user) {
          navigate('/login');
        }
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchData();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (user) {
      setFormData(prevData => ({
        ...prevData,
        name: user.name || '',
        birthYear: user.birthYear || '',
        location: user.location || ''
      }));
    }
  }, [user]);

  const currentYear= new Date().getFullYear();

  const [formData, setFormData] = useState({
    name: '',
    birthYear: '',
    location: ''
  });

  const [localErrors, setLocalErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.birthYear) errors.birthYear = 'Year of birth is required';
    if (!formData.location) errors.location = 'Location is required';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setLocalErrors(validationErrors);
      return;
    }

    try {
  await dispatch(updateUser(formData)).unwrap();

  if (userState?.role === 'kid') {
    if (!userCanvases || !userCanvases.owned || userCanvases.owned.length === 0) {
      navigate("/create-canvas");
    } else {
      navigate("/dashboard");
    }
  } else {
    // For non-kid users: go to dashboard if they have a plan or any invites, else to subscription
    if (currentPlan || (receivedInvites && receivedInvites.length > 0)) {
      if (!userCanvases || !userCanvases.owned || userCanvases.owned.length === 0) {
      navigate("/create-canvas");
    } else {
      navigate("/dashboard");
    }
    } else {
      navigate("/user-subscription");
    }
  }
    } catch (err) {
      console.error('Profile update failed:', err);
    }
  };

  // Show loading state
  if (isDataLoading || userLoading) {
    return (
      <div className="fixed inset-0 min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading profile data...</p>
        </div>
      </div>
    );
  }

  return (
    
    <div className="fixed inset-0 min-h-screen flex items-center justify-center bg-gray-50 px-4">
       {/* Logo container with smaller responsive sizing */}
    <div className="fixed top-0 left-0 p-4">
      <img 
        src={logo} 
        alt="Cash Canvas Logo" 
        className="h-24 w-auto object-contain max-w-[150px]"
      />
    </div>
      <div className="card-setup">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="txt-page-heading">
            Create Your profile
          </h1>
        </div>

        {/* Form */}
        <form className="w-full sm:w-3/4 md:w-2/3 lg:w-1/2 mx-auto space-y-6 pb-20" onSubmit={handleSubmit}>
          {/* Name Input */}
          <div>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter Your Full Name"
              required
              className="input-field"
            />
          </div>

          {/* Year of Birth */}
          <div>
            <select
              value={formData.birthYear}
              onChange={(e) => setFormData({ ...formData, birthYear: e.target.value })}
              className="input-field"
            >
              <option className="text-gray-400" value="">Select Birth Year</option>
              {Array.from({ length: currentYear - 1900 }, (_, i) => (
                <option className="text-gray-700" key={currentYear - i} value={currentYear - i}>
                  {currentYear - i}
                </option>
              ))}
            </select>
            {localErrors.birthYear && (
              <p className="mt-1 text-sm text-red-600">{localErrors.birthYear}</p>
            )}
          </div>

          {/* Location */}
          <div>
            <select
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="input-field"
            >
              <option value="">Select Your Location</option>
               {locationOptions
                  .slice()
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map(option => (
                    <option className="text-gray-700" key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
            </select>
            {localErrors.location && (
              <p className="mt-1 text-sm text-red-600">{localErrors.location}</p>
            )}
          </div>
        </form>

        {/* Button */}
        <div className="absolute bottom-6 right-6">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={userLoading || isDataLoading}
            className={`btn-continue ${(userLoading || isDataLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {userLoading || isDataLoading ? 'Loading...' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetupPage;